import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { Avatar } from '#/components/Avatar/Avatar';
import { ConditionalStyledLinkWithRef } from '#/components/Link/ConditionalStyledLinkWithRef';
import { Body } from '#/components/Text/Body';
import { H4 } from '#/components/Text/H4';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import type { DatetimeNoTzString } from '#/types/TypedDateString';
import { splitDatetimeNoTzString } from '#/utils/string/datetime/datetimeNoTzString';
import { humanDatetime } from '#/utils/string/datetime/datetimeString';

type Props = {
  slug?: string;
  name?: string;
  image?: ApiFilestackImageData;
  homePageUrl?: string;
  postedDate: DatetimeNoTzString;
  showDate: boolean;
  small?: boolean;
};

export function Byline({
  slug,
  name,
  image,
  homePageUrl,
  postedDate: postedDateNoTz,
  showDate,
  small,
}: Props) {
  const { locale } = useI18n();

  const { date: postedDate, time: postedTime } =
    splitDatetimeNoTzString(postedDateNoTz);

  return (
    <div
      data-qa-id="byline"
      style={{ display: 'flex', alignItems: 'center', flex: '2' }}
    >
      {image && name && (
        <div data-qa-id="byline-image" style={{ marginRight: 16 }}>
          <Avatar name={name} image={image} size="sm" />
        </div>
      )}
      <div>
        {name && (
          <H4 qaId="author-name" style={{ margin: 0, lineHeight: 1 }}>
            <ConditionalStyledLinkWithRef
              qaId="author-name-link"
              link={
                homePageUrl && slug
                  ? `/${homePageUrl}?author.slug=${slug}&pq=`
                  : ''
              }
            >
              <Body
                styleAs={small ? 'bodySmall' : undefined}
                style={{ lineHeight: 1.33 }}
              >
                {name}
              </Body>
            </ConditionalStyledLinkWithRef>
          </H4>
        )}
        {showDate && (
          <Text
            tag="time"
            styleAs={small ? 'bodyMicro' : 'bodySmall'}
            style={{ color: colors.lightContentGrey }}
          >
            {humanDatetime({ date: postedDate, time: postedTime }, { locale })}
          </Text>
        )}
      </div>
    </div>
  );
}
